<template>
  <!-- Mounted once in App.vue so the power dialer keeps running (and its
       call/wrap-up UI stays reachable) while the rep uses the rest of the
       app. The status banner lives in DefaultLayout instead, since it's
       positioned against the sidenav/navbar that only that layout has. The
       /dialer page only shows the queue and session summary. -->
  <Modal
    v-if="dialer.wrapupTarget"
    :show="true"
    size="xs"
    :showCloseButton="false"
    :closeOnBackdrop="false"
    :closeOnEscape="false"
  >
    <template #header>
      <h2 class="dialer-wrapup__title">Save Call Disposition</h2>
    </template>
    <template #body>
      <div class="dialer-wrapup__body">
        <p>Please select a disposition for this call before continuing.</p>
        <Select
          v-model="dialer.selectedDisposition"
          :options="callDispositions"
          placeholder="Choose call disposition"
          label-key="name"
          value-key="id"
          :return-object="true"
        />
        <Textarea v-model="dialer.wrapupNotes" placeholder="Notes (optional)" :rows="3" />
      </div>
    </template>
    <template #footer>
      <Button
        variant="primary"
        :disabled="!dialer.selectedDisposition || dialer.isSubmittingWrapup"
        :loading="dialer.isSubmittingWrapup"
        @click="dialer.submitWrapupForm"
      >
        {{ dialer.isSubmittingWrapup ? "Saving..." : "Save Disposition" }}
      </Button>
    </template>
  </Modal>

  <ActionConfirmationModal
    :show="dialer.showEndSessionConfirm"
    title="End dialer session?"
    message="You won't be able to resume it afterward."
    confirm-text="End Session"
    cancel-text="Cancel"
    confirm-variant="danger"
    :is-loading="dialer.isEndingSession"
    @close="dialer.showEndSessionConfirm = false"
    @confirm="dialer.confirmEndSession"
  />

  <CallSessionModal
    :icps="[]"
    :show="dialer.showCallModal"
    :contactName="dialer.currentContactName"
    :to="dialer.to"
    :status="dialer.callStatus"
    :isPreparingAIContext="false"
    :showAIPrompt="false"
    :incomingCallData="null"
    formattedIncomingCallTimer="00:00:00"
    :formattedCallTimer="dialer.formattedCallTimer"
    :isMuted="dialer.isMuted"
    :saveRecording="dialer.saveRecording"
    :enableTranscription="enableTranscription"
    :parentCallSid="dialer.parentCallSid"
    :currentCallSid="dialer.currentCallSid"
    :account="account"
    :microphones="dialer.microphones"
    :speakers="dialer.speakers"
    :selectedMicId="dialer.selectedMicId"
    :selectedSpeakerId="dialer.selectedSpeakerId"
    :isThinking="aiIsThinking"
    :isStreaming="aiIsStreaming"
    :hasSuggestions="aiHasSuggestions"
    :currentStreamingText="aiStreamingText"
    :aiSuggestions="aiSuggestions"
    @close="dialer.handleCloseCallModal"
    @hangup="dialer.hangupCall"
    @toggle-record="dialer.toggleRecording"
    @toggle-mute="dialer.toggleMute"
    @transfer-call="dialer.handleTransfer"
    @select-microphone="dialer.selectMicrophone"
    @select-speaker="dialer.selectSpeaker"
    @send-digit="dialer.sendDigit"
  />
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useStore } from "vuex";
import { Button, Modal, Select, Textarea } from "@/components/ui";
import ActionConfirmationModal from "@/components/modal/ActionConfirmationModal.vue";
import CallSessionModal from "@/components/call/CallSessionModal.vue";
import { useCallDispositions } from "@/composables/calls";
import { createPowerDialer } from "@/composables/dialer";
import { useAIAssistant } from "@/composables/calls/useAIAssistant";

const store = useStore();

const account = computed(() => store.getters.account.data);
const enableTranscription = computed(() => account.value?.enableTranscription !== false);

const { data: callDispositionsData } = useCallDispositions();
const callDispositions = computed(() => callDispositionsData.value || []);

const dialer = createPowerDialer();

// Only populated while the session has the AI call assistant on and a call is live.
const {
  isThinking: aiIsThinking,
  isStreaming: aiIsStreaming,
  hasSuggestions: aiHasSuggestions,
  currentStreamingText: aiStreamingText,
  suggestions: aiSuggestions,
} = useAIAssistant();
</script>

<style scoped>
.dialer-wrapup__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a2e;
}

.dialer-wrapup__body {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.dialer-wrapup__body p {
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
}
</style>
